// @ts-nocheck
import { PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from "react";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import Autocomplete from "@material-ui/lab/Autocomplete";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import { Grid, Typography, Box, makeStyles, TextField, Popper } from "@material-ui/core";
import { SnackBarContext } from "components/snack-bar";
import { Icon } from "components/icons";
import { VisiblePostCodeType } from "store/types";

import { styles } from "./style";
import {
  CityType,
  GetDetailsCallbackType,
  GetPlacePredictionsType,
  GetPlacePredictionRequestType,
  GetPlacePredictionCallbackType,
  PlaceAutoCompleteOption,
  PartsType,
  LocationLatLngType,
  ParsedAddressFields,
} from "./type.js";
import { parsePlaceDetailsToAddressFields } from "./parseAddressComponents";

const useStyles = makeStyles(() => styles);

type PlaceAutocompleteProps = PropsWithChildren<{
  city?: CityType;
  countryCode?: string | null;
  disabled?: boolean;
  key?: string;
  markerInputValue?: PlaceAutoCompleteOption;
  restrictedIds?: string[];
  placeholder?: string;
  postCodeSearch?: boolean;
  handleRemoveAddress?: () => void;
  onLoad?: React.ReactEventHandler<HTMLDivElement>;
  onPlaceChanged?: (newMarker: LocationLatLngType) => void;
  /** When provided, called with parsed address fields (address1, address2, city, state, zip) after place details are fetched. Used e.g. for return address. */
  onPlaceChangedWithAddress?: (fields: ParsedAddressFields) => void;
  getFilteredPostCodes?: (key: string) => VisiblePostCodeType[];
  noOptionText?: string;
  disableAutoClear?: boolean;
  /** Test/automation anchor placed on the underlying input element. */
  dataCy?: string;
  iconSize?: string;
  optionFontSize?: string;
  popperMinWidth?: number;
}>;

export const PlaceAutocomplete = ({
  city = undefined,
  countryCode = null,
  disabled = false,
  key = undefined,
  markerInputValue = undefined,
  restrictedIds = undefined,
  dataCy = undefined,
  placeholder,
  postCodeSearch = false,
  handleRemoveAddress = () => {
    console.log("We dont have function handleRemoveAddress");
  },
  onLoad = undefined,
  onPlaceChanged = () => {},
  onPlaceChangedWithAddress = undefined,
  getFilteredPostCodes = () => [],
  noOptionText = "Start typing to see suggestions",
  disableAutoClear,
  iconSize = "m",
  optionFontSize,
  popperMinWidth,
}: PlaceAutocompleteProps) => {
  const classes = useStyles();
  const [value, setValue] = useState<PlaceAutoCompleteOption | null>(markerInputValue ? markerInputValue : null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<PlaceAutoCompleteOption[]>([]);
  const [popperWidth, setPopperWidth] = useState(100);

  const runSnackBar = useContext(SnackBarContext);

  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  const resetAllValues = () => {
    setValue(null);
    setInputValue("");
    setOptions([]);
  };

  const fetchPlacePredictions: GetPlacePredictionsType = useMemo(
    () =>
      throttle((request: GetPlacePredictionRequestType, callback: GetPlacePredictionCallbackType) => {
        autocompleteService.current?.getPlacePredictions(request, callback);
      }, 200),
    []
  );

  const getPlaceDetails: GetDetailsCallbackType = (placeDetails) => {
    onPlaceChanged({
      formattedAddress: placeDetails?.formatted_address || "",
      geometry: {
        location: {
          lat: () => placeDetails?.geometry?.location?.lat() || 0,
          lng: () => placeDetails?.geometry?.location?.lng() || 0,
        },
      },
    });
    if (onPlaceChangedWithAddress && placeDetails) {
      const fields = parsePlaceDetailsToAddressFields(placeDetails);
      onPlaceChangedWithAddress(fields);
    }
  };

  const handleAutocompleteChange = (newValue: PlaceAutoCompleteOption | null) => {
    if (!placesService.current) {
      return undefined;
    }

    if (newValue) {
      if (!newValue.isPostcode) {
        const request = {
          placeId: newValue.place_id,
        };
        placesService.current.getDetails(request, getPlaceDetails);
      } else if (postCodeSearch) {
        onPlaceChanged({
          formattedAddress: newValue?.description || "",
          geometry: {
            location: {
              lat: () => newValue.postCode?.center.lat || 0,
              lng: () => newValue.postCode?.center.lng || 0,
            },
          },
          isPostcode: true,
          postCode: newValue.postCode,
        });
      }

      setValue(newValue);
    } else {
      handleRemoveAddress();
    }

    if (disableAutoClear) return;
    setTimeout(() => {
      resetAllValues();
    }, 300);
  };

  const handleInputChange = (newInputValue: string) => {
    if (!newInputValue) {
      return resetAllValues();
    }

    if (!/^[a-zA-Z0-9àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ$@$!%*?&#^-_-,'. +()]+$/.test(newInputValue)) {
      runSnackBar({
        type: "error",
        msg: `Only Latin characters are allowed`,
        vertical: "top",
        horizontal: "right",
      });
      return resetAllValues();
    }

    setInputValue(newInputValue);

    if (!autocompleteService.current || newInputValue === "") {
      return undefined;
    }

    const request: GetPlacePredictionRequestType = {
      input: city ? `${city.nameWithState ? city.nameWithState : city.name}, ${newInputValue}` : newInputValue,
      language: "en",
      componentRestrictions: { country: countryCode },
    };

    fetchPlacePredictions(request, (results) => {
      let newOptions: PlaceAutoCompleteOption[] = [];

      if (value) {
        newOptions = [value];
      }

      if (results) {
        newOptions = [...newOptions, ...results];
      }

      if (restrictedIds?.length) {
        newOptions = newOptions.filter((newOption) => !restrictedIds.includes(newOption.place_id));
      }

      const postCodes = postCodeSearch
        ? getFilteredPostCodes(newInputValue).map((postCode) => ({
            description: postCode.name,
            matched_substrings: [],
            place_id: "",
            isPostcode: true,
            postCode: postCode,
          }))
        : [];

      setOptions([...postCodes, ...newOptions]);
    });
  };

  useEffect(() => {
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!placesService.current && window.google) {
      const map = document.createElement("div");
      placesService.current = new window.google.maps.places.PlacesService(map);
    }
  }, []);

  const noOptionsTextRender = (customText: string) => {
    if (options.length === 0) {
      if (!inputValue) {
        return customText;
      } else {
        return "No options found";
      }
    }
  };

  return (
    <Autocomplete
      id={key}
      onLoad={onLoad}
      className={classes.placesAutocomplete}
      classes={{ paper: classes.paper, option: classes.option, noOptions: classes.noOptions }}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      blurOnSelect={!disableAutoClear}
      noOptionsText={noOptionsTextRender(noOptionText)}
      clearOnBlur={!disableAutoClear}
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event, newValue) => handleAutocompleteChange(newValue)}
      onInputChange={(event, newInputValue) => handleInputChange(newInputValue)}
      disabled={disabled}
      PopperComponent={(props) => {
        const effectiveWidth =
          popperMinWidth && popperMinWidth > popperWidth ? popperMinWidth : popperWidth || undefined;
        return (
          <Popper
            {...props}
            placement="bottom-start"
            style={{ zIndex: 9999, width: effectiveWidth ? `${effectiveWidth}px` : undefined }}
          />
        );
      }}
      renderInput={(params) => {
        const inputWidth = params?.inputProps?.ref?.current?.offsetWidth;
        if (inputWidth) {
          setPopperWidth(inputWidth);
        }
        return (
          <Box className={classes.textFieldV2}>
            <Icon type="Search" size={iconSize} />
            <TextField
              {...params}
              // The anchor goes on the INPUT, not the wrapper: automation types
              // into the field itself, and MUI renders several nested nodes.
              inputProps={{ ...params.inputProps, ...(dataCy ? { "data-cy": dataCy } : {}) }}
              value={inputValue}
              // className={classes.textField}
              placeholder={placeholder}
              variant="outlined"
              size="small"
              fullWidth
            />
          </Box>
        );
      }}
      renderOption={(option) => {
        if (!option.isPostcode) {
          const matches = option.structured_formatting?.main_text_matched_substrings || [];
          const parts: PartsType[] = parse(
            option.structured_formatting?.main_text,
            matches.map((match) => [match.offset, match.offset + match.length])
          );

          return (
            <Grid container alignItems="center" style={optionFontSize ? { fontSize: optionFontSize } : undefined}>
              <Grid item>
                <LocationOnIcon className={classes.icon} />
              </Grid>
              <Grid item xs>
                {parts.map((part: any, index: number) => (
                  <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                    {part.text}
                  </span>
                ))}

                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={optionFontSize ? { fontSize: optionFontSize } : undefined}
                >
                  {option.structured_formatting?.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          );
        }

        return (
          <Grid container alignItems="center" style={optionFontSize ? { fontSize: optionFontSize } : undefined}>
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              <Typography style={{ fontWeight: 700, ...(optionFontSize ? { fontSize: optionFontSize } : {}) }}>
                {option.description}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
};
